import type { ModuleDashboardStepSpec } from './dashboardModuleTour.js';

export const COORDINATOR_LAB_TOUR_SLUGS = [
  'coordinator_create_event',
  'coordinator_cancel_event',
  'coordinator_create_user',
  'coordinator_manage_venues',
  'coordinator_embed_calendar',
  'coordinator_agency_profile',
  'coordinator_certificates',
  'coordinator_outside_training',
] as const;

export type CoordinatorLabTourSlug = (typeof COORDINATOR_LAB_TOUR_SLUGS)[number];

/** Required platform tours before starting a Learning Lab mission. */
export const LAB_MISSION_PREREQUISITES = [
  'welcome_quick_start',
  'dashboard_coordinator',
] as const;

/** Progress alias: legacy slug maps to coordinator_create_event for one release. */
export const LEGACY_EVENTS_CREATE_FIRST_SLUG = 'events_create_first';
export const EVENTS_CREATE_FIRST_PROGRESS_ALIAS = 'coordinator_create_event';

/** @deprecated Mission steps compile from learningLabPlaybooks.ts — kept for legacy anchor references. */
export const COORDINATOR_LAB_TOUR_SPECS: Record<
  CoordinatorLabTourSlug,
  readonly ModuleDashboardStepSpec[]
> = {
  coordinator_create_event: [
    {
      stepName: 'lab_event_create_nav',
      tourAnchor: 'coordinator-create-event-btn',
      title: 'Open event create',
      content: 'Coordinators create training from the event create flow.',
      module: 'coordinator',
      coordinatorTabSlug: 'events',
    },
    {
      stepName: 'lab_event_form_stepper',
      tourAnchor: 'event-form-stepper',
      title: 'Event steps',
      content: 'Walk through the six-step builder (basics through review) before publishing.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_event_form_description',
      tourAnchor: 'event-form-description',
      title: 'Event details',
      content: 'Title, description, and schedule live on this step.',
      isPanelTarget: true,
    },
  ],
  coordinator_cancel_event: [
    {
      stepName: 'lab_cancel_events_tab',
      tourAnchor: 'coordinator-tab-events',
      title: 'Agency events',
      content: 'Published and draft events appear here.',
      module: 'coordinator',
      coordinatorTabSlug: 'events',
    },
    {
      stepName: 'lab_cancel_row',
      tourAnchor: 'coordinator-event-row-event_leadership_cancel',
      title: 'Select an event',
      content: 'Choose the event you need to cancel.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_cancel_button',
      tourAnchor: 'coordinator-event-cancel-btn',
      title: 'Cancel action',
      content: 'Open the cancellation dialog — practice mode shows a sample flow only.',
      isPanelTarget: true,
    },
  ],
  coordinator_create_user: [
    {
      stepName: 'lab_members_tab',
      tourAnchor: 'coordinator-tab-members',
      title: 'Agency members',
      content: 'Manage roster members from this tab.',
      module: 'coordinator',
      coordinatorTabSlug: 'members',
    },
    {
      stepName: 'lab_create_user_btn',
      tourAnchor: 'coordinator-create-user-btn',
      title: 'Add a member',
      content: 'Open the create-user dialog to invite a new member.',
      isPanelTarget: true,
    },
  ],
  coordinator_manage_venues: [
    {
      stepName: 'lab_venues_tab',
      tourAnchor: 'coordinator-tab-venues',
      title: 'Venues',
      content: 'Agency venues used on events are managed here.',
      module: 'coordinator',
      coordinatorTabSlug: 'venues',
    },
    {
      stepName: 'lab_venue_list',
      tourAnchor: 'coordinator-venue-list',
      title: 'Venue list',
      content: 'Sample venues are pre-loaded in the Learning Lab.',
      isPanelTarget: true,
    },
  ],
  coordinator_embed_calendar: [
    {
      stepName: 'lab_embed_tab',
      tourAnchor: 'coordinator-tab-embed',
      title: 'Embed calendar',
      content: 'Share a public calendar widget on your website.',
      module: 'coordinator',
      coordinatorTabSlug: 'embed',
    },
    {
      stepName: 'lab_embed_settings',
      tourAnchor: 'embed-tab-settings',
      title: 'Settings',
      content: 'Configure colors, layout, and defaults.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_embed_code',
      tourAnchor: 'embed-tab-code',
      title: 'Embed code',
      content: 'Copy the snippet to paste on your site.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_embed_preview',
      tourAnchor: 'embed-tab-preview',
      title: 'Preview',
      content: 'See how the calendar widget looks before you publish embed code.',
      isPanelTarget: true,
    },
  ],
  coordinator_agency_profile: [
    {
      stepName: 'lab_settings_tab',
      tourAnchor: 'coordinator-tab-settings',
      title: 'Agency settings',
      content: 'Update agency profile and contacts.',
      module: 'coordinator',
      coordinatorTabSlug: 'settings',
    },
    {
      stepName: 'lab_agency_description',
      tourAnchor: 'coordinator-agency-description',
      title: 'Agency description',
      content: 'Public-facing agency details appear on your profile.',
      isPanelTarget: true,
    },
  ],
  coordinator_certificates: [
    {
      stepName: 'lab_certificates_tab',
      tourAnchor: 'coordinator-tab-certificates',
      title: 'Certificates',
      content: 'Templates and issued certificates for your agency.',
      module: 'coordinator',
      coordinatorTabSlug: 'certificates',
    },
    {
      stepName: 'lab_cert_template_list',
      tourAnchor: 'coordinator-cert-templates',
      title: 'Templates',
      content: 'Sample templates are seeded in the Learning Lab.',
      isPanelTarget: true,
    },
  ],
  coordinator_outside_training: [
    {
      stepName: 'lab_outside_tab',
      tourAnchor: 'coordinator-tab-outside-training',
      title: 'Outside Training',
      content: 'Review training members completed outside APT.',
      module: 'coordinator',
      coordinatorTabSlug: 'outside-training',
    },
    {
      stepName: 'lab_outside_row',
      tourAnchor: 'coordinator-outside-training-row-pending',
      title: 'Pending record',
      content: 'A sample pending submission is seeded for practice.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_outside_review',
      tourAnchor: 'coordinator-outside-training-review-btn',
      title: 'Open review',
      content: 'Open the review dialog for the pending record.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_outside_modal',
      tourAnchor: 'coordinator-outside-training-review-modal',
      title: 'Review dialog',
      content: 'Approve or reject with optional notes — practice mode only.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_outside_notes',
      tourAnchor: 'coordinator-outside-training-review-notes',
      title: 'Review notes',
      content: 'Add optional notes about the training record.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_outside_submit',
      tourAnchor: 'coordinator-outside-training-review-submit-btn',
      title: 'Submit review',
      content: 'Submit is intercepted in the Lab with a practice message.',
      isPanelTarget: true,
    },
    {
      stepName: 'lab_outside_complete',
      tourAnchor: 'body',
      title: 'Complete',
      content: 'You practiced the outside-training review workflow.',
    },
  ],
};

export function isCoordinatorLabTourSlug(
  slug: string,
): slug is CoordinatorLabTourSlug {
  return (COORDINATOR_LAB_TOUR_SLUGS as readonly string[]).includes(slug);
}
